import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { fetchNwssSignalSamples } from "./fetchNwssSignalSamples"
import {
  countyKeysForSample,
  reportingCountiesForSample,
} from "./nwssCountyMatch"
import type { NwssSignalSample, NwssSignalSampleList } from "./nwssSampleTypes"
import {
  buildCountyRollingRates,
  buildSiteBubbleTimeline,
  filterSamplesToChartLookback,
  NWSS_SITE_DETECTIONS_LOOKBACK_MONTHS,
  weekEndKey,
  type NwssRollingDetectionRateData,
  type NwssSiteBubbleTimelineData,
} from "./nwssWeeklyCharts"
import { dayjsUTC } from "../../utils/dates"
import type { NwssDrawerSampleScope } from "./nwssDrawerScope"

export type { NwssDrawerSampleScope } from "./nwssDrawerScope"

/**
 * Wraps `/nwss/biothreat-signals/{event_id}/samples` and shapes raw sampling
 * history into detection-focused view-models for the drawer (heatmaps, stats).
 */

export interface NwssSiteSummary {
  site: string
  /** Reporting county name(s) for this site, comma-separated when multiple. */
  county: string | null
  sampleCount: number
  detectionCount: number
  /** Most recent positive sample date for this site. */
  latestDetectionDate: string | null
  /** Most recent sample collection date for this site (any result). */
  latestSampleDate: string | null
  /** Days from last detection to the latest sample in this view; null if never positive. */
  daysSinceLastDetection: number | null
}

export interface NwssCountySummary {
  county: string
  sampleCount: number
  detectionCount: number
  latestDetectionDate: string | null
  latestSampleDate: string | null
  daysSinceLastDetection: number | null
}

export interface NwssHeatmap {
  /** Sites in natural sort order (drawer renders the top slice). */
  sites: string[]
  weekKeys: string[]
  weekLabels: string[]
  /** `${site}__${weekKey}` → detection count that week. */
  cells: Map<string, number>
  maxCount: number
}

export interface NwssSamplesStats {
  sampleCount: number
  detectionCount: number
  memberCount: number
  siteCount: number
  /** Positivity over the trailing 14 collection days ending at the latest sample. */
  positivityPct: number
  /** Change in positivity vs the prior 14-day window (percentage points). */
  positivityTrendPct: number | null
  detectionDays: number
}

const POSITIVITY_LOOKBACK_DAYS = 14

export interface NwssSignalSamplesOptions {
  /** State-event reporting counties from ``nwss_meta.counties`` (SOURCE tooltip). */
  reportingCounties?: string[]
  /** API context padding; default 180 when omitted. */
  contextDays?: number
  /** For chart titles and county rolling fallback. */
  resolvedGeo?: string
}

export interface NwssSignalSamplesResult {
  /** Full API payload (may exceed chart lookback when truncated). */
  samples: NwssSignalSample[]
  windowStart: string | null
  windowEnd: string | null
  truncated: boolean
  /** When set, county views are limited to these names (grid SOURCE list). */
  reportingCounties: string[] | null
  stats: NwssSamplesStats
  siteSummaries: NwssSiteSummary[]
  countySummaries: NwssCountySummary[]
  countyHeatmap: NwssHeatmap
  siteBubbleTimeline: NwssSiteBubbleTimelineData
  /** County tab + county-level site-tab rolling (24-month lookback). */
  countyRollingRates: NwssRollingDetectionRateData[]
  /** Site tab rolling when shown (12-month lookback). */
  siteTabRollingRates: NwssRollingDetectionRateData[]
  isLoading: boolean
  isError: boolean
}

const DEFAULT_CONTEXT_DAYS = 180

export const useNwssSignalSamples = (
  eventId: string | null,
  options: NwssSignalSamplesOptions = {}
): NwssSignalSamplesResult => {
  const reportingCounties = useMemo((): string[] | null => {
    const counties = options.reportingCounties
    return counties?.length ? [...counties] : null
  }, [options.reportingCounties])

  const contextDays = options.contextDays ?? DEFAULT_CONTEXT_DAYS
  const queryParams = useMemo(
    () => ({
      include_non_detects: true as const,
      context_days: contextDays,
    }),
    [contextDays]
  )

  const key = eventId ?? ""
  const query = useQuery({
    queryKey: [`/nwss/biothreat-signals/${key}/samples`, queryParams] as const,
    enabled: Boolean(eventId),
    queryFn: ({ signal }) => fetchNwssSignalSamples(key, queryParams, signal),
  })

  const data = query.data
  const truncated = data?.truncated ?? false
  const samples = useMemo((): NwssSignalSample[] => data?.results ?? [], [data])
  const resolvedGeo = options.resolvedGeo ?? ""

  const views = useMemo(
    () =>
      deriveNwssSamplesViews(samples, {
        data,
        reportingCounties,
        resolvedGeo,
        scope: "historic",
      }),
    [samples, data, reportingCounties, resolvedGeo]
  )

  return {
    samples,
    windowStart: data?.window_start ?? null,
    windowEnd: data?.window_end ?? null,
    truncated,
    reportingCounties,
    ...views,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

export const filterNwssSamplesByScope = (
  samples: NwssSignalSample[],
  scope: NwssDrawerSampleScope
): NwssSignalSample[] =>
  scope === "event" ? samples.filter(sample => sample.is_event_member) : samples

export const deriveNwssSamplesViews = (
  samples: NwssSignalSample[],
  {
    data,
    reportingCounties,
    resolvedGeo,
    scope = "historic",
  }: {
    data?: NwssSignalSampleList
    reportingCounties: string[] | null
    resolvedGeo: string
    scope?: NwssDrawerSampleScope
  }
): Pick<
  NwssSignalSamplesResult,
  | "stats"
  | "siteSummaries"
  | "countySummaries"
  | "countyHeatmap"
  | "siteBubbleTimeline"
  | "countyRollingRates"
  | "siteTabRollingRates"
> => {
  const siteSummaries = buildSiteSummaries(samples, reportingCounties)
  const countySummaries = buildCountySummaries(samples, reportingCounties)
  const siteRows = siteSummaries.map(summary => summary.site)

  return {
    stats: buildNwssSamplesStats(samples, data, scope),
    siteSummaries,
    countySummaries,
    countyHeatmap: buildCountyHeatmap(
      samples,
      countySummaries,
      reportingCounties
    ),
    siteBubbleTimeline: buildSiteBubbleTimeline(samples, siteRows),
    countyRollingRates: buildCountyRollingRates(
      samples,
      reportingCounties,
      resolvedGeo
    ),
    siteTabRollingRates: buildCountyRollingRates(
      samples,
      reportingCounties,
      resolvedGeo,
      NWSS_SITE_DETECTIONS_LOOKBACK_MONTHS
    ),
  }
}

export const applyNwssSampleScope = (
  result: NwssSignalSamplesResult,
  scope: NwssDrawerSampleScope,
  resolvedGeo: string,
  data?: NwssSignalSampleList
): NwssSignalSamplesResult => {
  if (scope === "historic") return result

  const samples = filterNwssSamplesByScope(result.samples, scope)
  const views = deriveNwssSamplesViews(samples, {
    data,
    reportingCounties: result.reportingCounties,
    resolvedGeo,
    scope,
  })

  return { ...result, samples, ...views }
}

const buildSiteSummaries = (
  samples: NwssSignalSample[],
  reportingCounties: string[] | null
): NwssSiteSummary[] => {
  const referenceDate = latestSampleDateFromRows(samples)
  const bySite = new Map<string, NwssSignalSample[]>()
  for (const sample of samples) {
    const rows = bySite.get(sample.site) ?? []
    rows.push(sample)
    bySite.set(sample.site, rows)
  }
  const summaries = [...bySite.entries()].map(([site, rows]) => {
    const latestDetectionDate = latestDetectionDateFromRows(rows)
    return {
      site,
      county: nwssCountyLabelForSite(samples, site, reportingCounties),
      sampleCount: rows.length,
      detectionCount: rows.filter(row => row.is_detected).length,
      latestDetectionDate,
      latestSampleDate: latestSampleDateFromRows(rows),
      daysSinceLastDetection: daysSinceLastDetection(
        latestDetectionDate,
        referenceDate
      ),
    }
  })
  return summaries.sort((a, b) => compareNwssSites(a.site, b.site))
}

const buildCountySummaries = (
  samples: NwssSignalSample[],
  reportingCounties: string[] | null
): NwssCountySummary[] => {
  const referenceDate = latestSampleDateFromRows(samples)

  if (reportingCounties?.length) {
    const byCounty = new Map<string, NwssSignalSample[]>(
      reportingCounties.map(county => [county, []])
    )
    for (const sample of samples) {
      for (const county of reportingCountiesForSample(
        sample,
        reportingCounties
      )) {
        byCounty.get(county)?.push(sample)
      }
    }
    const summaries = reportingCounties.map(county => {
      const rows = byCounty.get(county) ?? []
      const latestDetectionDate = latestDetectionDateFromRows(rows)
      return {
        county,
        sampleCount: rows.length,
        detectionCount: rows.filter(row => row.is_detected).length,
        latestDetectionDate,
        latestSampleDate: latestSampleDateFromRows(rows),
        daysSinceLastDetection: daysSinceLastDetection(
          latestDetectionDate,
          referenceDate
        ),
      }
    })
    return summaries.sort((a, b) => compareNwssCounties(a.county, b.county))
  }

  const byCounty = new Map<string, NwssSignalSample[]>()
  for (const sample of samples) {
    for (const county of countyKeysForSample(sample)) {
      const rows = byCounty.get(county) ?? []
      rows.push(sample)
      byCounty.set(county, rows)
    }
  }
  const summaries = [...byCounty.entries()].map(([county, rows]) => {
    const latestDetectionDate = latestDetectionDateFromRows(rows)
    return {
      county,
      sampleCount: rows.length,
      detectionCount: rows.filter(row => row.is_detected).length,
      latestDetectionDate,
      latestSampleDate: latestSampleDateFromRows(rows),
      daysSinceLastDetection: daysSinceLastDetection(
        latestDetectionDate,
        referenceDate
      ),
    }
  })
  return summaries.sort((a, b) => compareNwssCounties(a.county, b.county))
}

const accumulateDetectionCells = (
  samples: NwssSignalSample[],
  rowKeysForSample: (sample: NwssSignalSample) => string[]
): { weeks: Set<string>; cells: Map<string, number> } => {
  const weeks = new Set<string>()
  const cells = new Map<string, number>()
  for (const sample of samples) {
    const week = weekEndKey(sample.sample_collect_date)
    weeks.add(week)
    if (!sample.is_detected) continue
    for (const row of rowKeysForSample(sample)) {
      const cellKey = `${row}__${week}`
      cells.set(cellKey, (cells.get(cellKey) ?? 0) + 1)
    }
  }
  return { weeks, cells }
}

const formatNwssHeatmap = (
  rowLabels: string[],
  weeks: Set<string>,
  cells: Map<string, number>
): NwssHeatmap => {
  const weekKeys = [...weeks].sort()
  return {
    sites: rowLabels,
    weekKeys,
    weekLabels: weekKeys.map(week => dayjsUTC(week).format("MMM D")),
    cells,
    maxCount: Math.max(1, ...cells.values()),
  }
}

const buildCountyHeatmap = (
  samples: NwssSignalSample[],
  countySummaries: NwssCountySummary[],
  reportingCounties: string[] | null
): NwssHeatmap => {
  const windowSamples = filterSamplesToChartLookback(samples)
  const rowKeysForSample = reportingCounties?.length
    ? (sample: NwssSignalSample) =>
        reportingCountiesForSample(sample, reportingCounties)
    : countyKeysForSample
  const { weeks, cells } = accumulateDetectionCells(
    windowSamples,
    rowKeysForSample
  )
  return formatNwssHeatmap(
    countySummaries.map(summary => summary.county),
    weeks,
    cells
  )
}

const latestDetectionDateFromRows = (
  rows: NwssSignalSample[]
): string | null => {
  const detectionDates = rows
    .filter(row => row.is_detected)
    .map(row => row.sample_collect_date)
  if (detectionDates.length === 0) return null
  return detectionDates.sort().at(-1) ?? null
}

const latestSampleDateFromRows = (rows: NwssSignalSample[]): string | null =>
  rows.reduce<string | null>(
    (max, row) =>
      !max || row.sample_collect_date > max ? row.sample_collect_date : max,
    null
  )

/** Elapsed whole days from last detection through the latest sample in the drawer view. */
const daysSinceLastDetection = (
  latestDetectionDate: string | null,
  referenceDate: string | null
): number | null => {
  if (!latestDetectionDate || !referenceDate) return null
  const days = dayjsUTC(referenceDate).diff(
    dayjsUTC(latestDetectionDate),
    "day"
  )
  return Math.max(0, days)
}

const positivityPctForDateRange = (
  samples: NwssSignalSample[],
  rangeStart: string,
  rangeEnd: string
): number | null => {
  const inRange = samples.filter(
    sample =>
      sample.sample_collect_date >= rangeStart &&
      sample.sample_collect_date <= rangeEnd
  )
  if (inRange.length === 0) return null
  const positives = inRange.filter(sample => sample.is_detected).length
  return (100 * positives) / inRange.length
}

const buildNwssSamplesStats = (
  samples: NwssSignalSample[],
  data: NwssSignalSampleList | undefined,
  scope: NwssDrawerSampleScope = "historic"
): NwssSamplesStats => {
  const isTruncated = data?.truncated ?? false
  const detections = samples.filter(sample => sample.is_detected)
  const memberCountFromSamples = samples.filter(
    sample => sample.is_event_member
  ).length
  const useSampleMemberCount = scope === "event" || isTruncated

  const latestCollectDate = samples.reduce<string | null>(
    (max, sample) =>
      !max || sample.sample_collect_date > max
        ? sample.sample_collect_date
        : max,
    null
  )

  let positivityPct = 0
  let positivityTrendPct: number | null = null
  if (latestCollectDate) {
    const windowEnd = dayjsUTC(latestCollectDate)
    const recentStart = windowEnd
      .subtract(POSITIVITY_LOOKBACK_DAYS - 1, "day")
      .format("YYYY-MM-DD")
    const priorEnd = windowEnd
      .subtract(POSITIVITY_LOOKBACK_DAYS, "day")
      .format("YYYY-MM-DD")
    const priorStart = windowEnd
      .subtract(POSITIVITY_LOOKBACK_DAYS * 2 - 1, "day")
      .format("YYYY-MM-DD")

    const recentPct = positivityPctForDateRange(
      samples,
      recentStart,
      latestCollectDate
    )
    const priorPct = positivityPctForDateRange(samples, priorStart, priorEnd)
    positivityPct = recentPct ?? 0
    if (recentPct != null && priorPct != null) {
      positivityTrendPct = recentPct - priorPct
    }
  }

  return {
    sampleCount: samples.length,
    detectionCount: detections.length,
    memberCount:
      !useSampleMemberCount && data?.member_count != null
        ? data.member_count
        : memberCountFromSamples,
    siteCount: data?.site_count ?? new Set(samples.map(s => s.site)).size,
    positivityPct,
    positivityTrendPct,
    detectionDays: new Set(detections.map(sample => sample.sample_collect_date))
      .size,
  }
}

export { weekEndKey, filterSamplesToChartLookback }

export const formatPopulationServed = (
  value: number | null,
  emptyLabel = "—"
): string => (value == null ? emptyLabel : value.toLocaleString())

/** Natural sort for NWSS site ids (numeric segments order correctly). */
export const compareNwssSites = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })

/** Raw county field from the API for display (no prefix/suffix/normalization). */
export const nwssCountyDisplayValue = (sample: NwssSignalSample): string =>
  sample.counties_served ?? sample.county_fips

/** County field from the first sample at a site (for site labels). */
export const nwssCountyLabelForSite = (
  samples: NwssSignalSample[],
  site: string,
  reportingCounties: string[] | null = null
): string | null => {
  for (const sample of samples) {
    if (sample.site !== site) continue
    if (reportingCounties?.length) {
      const matched = reportingCountiesForSample(sample, reportingCounties)
      if (matched.length) return matched.join(", ")
      continue
    }
    const value = nwssCountyDisplayValue(sample)
    if (value) return value
  }
  return null
}

export {
  countyLabelsMatch,
  countyKeysForSample,
  reportingCountiesForSample,
} from "./nwssCountyMatch"

/** Display label for the raw-table county column when limited to reporting counties. */
export const nwssReportingCountyDisplay = (
  sample: NwssSignalSample,
  reportingCounties: string[] | null
): string => {
  if (!reportingCounties?.length) {
    return nwssCountyDisplayValue(sample) || "—"
  }
  const matched = reportingCountiesForSample(sample, reportingCounties)
  return matched.length ? matched.join(", ") : "—"
}

export const compareNwssCounties = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: "base" })

/** Detected samples in one heatmap cell (site or county × week). */
export const getNwssHeatmapCellDetections = (
  samples: NwssSignalSample[],
  row: string,
  weekKey: string,
  groupBy: "site" | "county",
  reportingCounties: string[] | null = null,
  lookbackMonths?: number
): NwssSignalSample[] => {
  const windowSamples = filterSamplesToChartLookback(samples, lookbackMonths)
  return windowSamples
    .filter(
      sample =>
        sample.is_detected &&
        weekEndKey(sample.sample_collect_date) === weekKey &&
        (groupBy === "site"
          ? sample.site === row
          : reportingCounties?.length
            ? reportingCountiesForSample(sample, reportingCounties).includes(
                row
              )
            : countyKeysForSample(sample).includes(row))
    )
    .sort((a, b) => {
      const byDate = a.sample_collect_date.localeCompare(b.sample_collect_date)
      if (byDate !== 0) return byDate
      return compareNwssSites(a.site, b.site)
    })
}
